import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { ENewsletterType, INewsletter } from '../../../models/newsletter.model';
import { IPaginationData } from '../../../api/base';
import newsletterApi from '../../../api/newsletter.api';
import moment from 'moment';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, Search, Visibility } from '@mui/icons-material';
import SendNewsletterModal from '../../../components/SendNewsletterModal';
import NewsletterDetailModal from '../../../components/NewsletterDetailModal';

export default function NewsletterListPage() {
  const [dataSource, setDataSource] = useState<IPaginationData<INewsletter>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
    newsletter_type: undefined as ENewsletterType | undefined,
  });

  const [loading, setLoading] = useState(false);
  const [[sortBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >([undefined, 'desc']);

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState<INewsletter | null>(null);

  const loadData = useCallback(() => {
    setLoading(true);
    newsletterApi
      .getList({
        ...filter,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: sortBy || 'sent_at',
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sortBy, order, filter]);

  useEffect(loadData, [loadData]);

  const theme = useTheme();

  const getNewsletterTypeLabel = (type: ENewsletterType) => {
    switch (type) {
      case ENewsletterType.Investor:
        return 'Investor';
      case ENewsletterType.Startup:
        return 'Start-up';
      case ENewsletterType.Researcher:
        return 'Researcher';
      default:
        return type;
    }
  };

  const getNewsletterTypeColor = (type: ENewsletterType): "primary" | "secondary" | "success" => {
    switch (type) {
      case ENewsletterType.Investor:
        return 'primary';
      case ENewsletterType.Startup:
        return 'secondary';
      case ENewsletterType.Researcher:
        return 'success';
      default:
        return 'primary';
    }
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const handleSendSuccess = () => {
    setSendModalOpen(false);
    loadData(); // Refresh the list
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Newsletter</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search newsletters"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <Button
            variant="contained"
            color="success"
            startIcon={<Add />}
            onClick={() => setSendModalOpen(true)}
          >
            Send New Newsletter
          </Button>
        </div>
      </Box>

      <Box sx={{ p: 2 }}>
        <Tabs
          value={filter.newsletter_type || 'all'}
          onChange={(e, value) => {
            setFilter({
              ...filter,
              page: 1,
              newsletter_type: value === 'all' ? undefined : value,
            });
          }}
          sx={{ mb: 2 }}
        >
          <Tab label="All" value="all" />
          <Tab label="Investor" value={ENewsletterType.Investor} />
          <Tab label="Start-up" value={ENewsletterType.Startup} />
          <Tab label="Researcher" value={ENewsletterType.Researcher} />
        </Tabs>
      </Box>

      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={sortBy}
        loading={loading}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'subject',
            title: 'Subject',
            dataKey: 'subject_en',
            render: (data) => (
              <Box sx={{ maxWidth: 200 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {data.subject_en}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.subject_vi}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'content',
            title: 'Content',
            dataKey: 'content_en',
            render: (data) => (
              <Box sx={{ maxWidth: 300 }}>
                <Typography variant="body2">
                  {truncateContent(data.content_en.replace(/<[^>]*>/g, ''))}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'newsletter_type',
            title: 'Type',
            dataKey: 'newsletter_type',
            sortable: true,
            render: (data) => (
              <Chip
                label={getNewsletterTypeLabel(data.newsletter_type)}
                color={getNewsletterTypeColor(data.newsletter_type)}
                size="small"
              />
            ),
          },
          {
            key: 'sent_at',
            title: 'Sent Date',
            dataKey: 'sent_at',
            sortable: true,
            render: (data) =>
              moment(data.sent_at).format('MM/DD/YYYY HH:mm'),
          },
          {
            key: 'recipient_count',
            title: 'Recipients',
            dataKey: 'recipient_count',
            sortable: true,
            render: (data) => (
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.recipient_count}
              </Typography>
            ),
          },
          {
            key: 'action',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <IconButton
                onClick={() => {
                  setSelectedNewsletter(data);
                  setDetailModalOpen(true);
                }}
                size="small"
              >
                <Visibility />
              </IconButton>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <SendNewsletterModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        onSuccess={handleSendSuccess}
      />

      <NewsletterDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        newsletter={selectedNewsletter}
      />
    </>
  );
}